import { gtmSDK } from 'iframes/gtm/GtmSDK';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { loaderHidden } from 'actions/loaderHidden';
import { successAttributionSubmitted } from 'actions/successAttributionSubmitted';

import responseTypes from 'consts/responseConsts';
import { CJ_EVENT_COOKIE, CONVERSION_TRACKED } from 'consts/storageConsts';

import { useTimeToCompleteEnrollment } from 'hooks/useCaptureTimeOnPage';

import { isBetween18And30 } from 'inputRules/dateOfBirth';

import {
  selectEnrollmentCashRewardsStatus,
  selectEnrollmentSuccessAttributionSubmitted,
} from 'selectors/enrollment';
import {
  selectFormData,
  selectFormDataDateOfBirth,
  selectFormDataPayType,
  selectFormDataSourceOfIncome,
} from 'selectors/form';
import { selectUserId } from 'selectors/user';

import { getHashedEmail } from 'utils/hashEmail';
import {
  executeOathScript,
  fireBingCustomConversionEvent,
  fireGtmCustomEvent,
  fireGtmDataLayerVariable,
  loadChimeDotComEnrollmentSuccess,
  loadGoogleAnalytics,
  loadGoogleTagManager,
} from 'utils/postEnrollmentHelper';
import { CJ_EVENT, IS_EMBEDDED, VALUE_PROP_QSP } from 'utils/queryParamHelpers';
import { sendSuccessPageView } from 'utils/segment/pageEvents';
import {
  track18To30EmployedEnrollment,
  trackCashRewardsQualifiedEnrollment,
  trackSuccessPageAttribution,
} from 'utils/segment/trackingEvents';
import cookies from 'utils/storage/cookies';
import {
  getValueFromStorage,
  setValueInStorage,
} from 'utils/storage/storageHelper';

export function useSuccessPageAttribution() {
  const payType = useSelector(selectFormDataPayType);
  const sourceOfIncome = useSelector(selectFormDataSourceOfIncome);
  const userId = useSelector(selectUserId);
  const wasSuccessAttributionSubmitted = useSelector(
    selectEnrollmentSuccessAttributionSubmitted
  );
  const dateOfBirth = useSelector(selectFormDataDateOfBirth);
  const formData = useSelector(selectFormData, shallowEqual);
  const cashRewardsEnrollmentStatus = useSelector(
    selectEnrollmentCashRewardsStatus
  );

  const isCashRewardsEnrollment =
    cashRewardsEnrollmentStatus === responseTypes.SUCCESSFUL_REWARDS_ENROLLMENT;

  const dispatch = useDispatch();

  useTimeToCompleteEnrollment();

  // Note: Despite the name, deprecatedFireSuccessAttribution is still actively used as of September 2025.
  // In 2024, this function was marked for deprecation in favor of fireSuccessAttribution, but the
  // migration was put on hold due to a GTM incident and still is due to the upcoming EC 2.0 enrollment flow release.
  function deprecatedFireSuccessAttribution(dontHideLoader) {
    if (!dontHideLoader) dispatch(loaderHidden());

    if (!wasSuccessAttributionSubmitted && IS_EMBEDDED) {
      sendSuccessPageView();
      loadGoogleAnalytics(true);

      if (isBetween18And30(dateOfBirth) && sourceOfIncome === 'Employment') {
        track18To30EmployedEnrollment(formData);
      }

      if (isCashRewardsEnrollment) {
        fireGtmCustomEvent({
          event: 'CR Qualified Enrollment',
        });

        trackCashRewardsQualifiedEnrollment();
      }

      if (!getValueFromStorage(CONVERSION_TRACKED)) {
        trackSuccessPageAttribution(formData);

        setValueInStorage(CONVERSION_TRACKED, 'true');
      }

      dispatch(successAttributionSubmitted());
    } else if (!wasSuccessAttributionSubmitted) {
      sendSuccessPageView();
      loadGoogleAnalytics(true);
      loadGoogleTagManager();

      const userHashedEmail = getHashedEmail();
      fireGtmDataLayerVariable({ userHashedEmail });
      fireGtmDataLayerVariable({ userId });
      fireGtmCustomEvent({ event: 'enrollmentSuccessPageViewed' });
      fireBingCustomConversionEvent();

      if (payType === 'Direct Deposit' && sourceOfIncome === 'Employment') {
        fireGtmCustomEvent({
          event: 'Employed Direct Depositor Enrolled',
        });
      }

      if (isBetween18And30(dateOfBirth) && sourceOfIncome === 'Employment') {
        fireGtmCustomEvent({
          event: '18-30 Employed Enrollment',
        });
        track18To30EmployedEnrollment(formData);
      }

      if (
        cashRewardsEnrollmentStatus ===
        responseTypes.SUCCESSFUL_REWARDS_ENROLLMENT
      ) {
        fireGtmCustomEvent({
          event: 'CR Qualified Enrollment',
        });

        trackCashRewardsQualifiedEnrollment();
      }

      let cjEventCookie = cookies.get(CJ_EVENT_COOKIE);

      if (CJ_EVENT || cjEventCookie) {
        fireGtmCustomEvent({
          event: 'CJ Event',
          memberId: userId,
          CJEVENT: CJ_EVENT ? CJ_EVENT : cjEventCookie,
          valueProp: VALUE_PROP_QSP,
        });
      }

      if (!IS_EMBEDDED) {
        executeOathScript(true);
      }

      if (!getValueFromStorage(CONVERSION_TRACKED)) {
        trackSuccessPageAttribution(formData);

        setValueInStorage(CONVERSION_TRACKED, 'true');
      }

      loadChimeDotComEnrollmentSuccess();

      dispatch(successAttributionSubmitted());
    }
  }

  function fireSuccessAttribution() {
    dispatch(loaderHidden());

    if (!wasSuccessAttributionSubmitted) {
      sendSuccessPageView();
      loadGoogleAnalytics(true);

      if (IS_EMBEDDED) {
        handleEmbeddedAttribution();
      } else {
        handleStandardAttribution();
      }

      dispatch(successAttributionSubmitted());
    }
  }

  function handleEmbeddedAttribution() {
    if (!getValueFromStorage(CONVERSION_TRACKED)) {
      trackSuccessPageAttribution(formData);

      setValueInStorage(CONVERSION_TRACKED, 'true');
    }
  }

  function handleStandardAttribution() {
    gtmSDK.sendEvent('fireCustomEvent', {
      event: { userHashedEmail: getHashedEmail() },
    });
    gtmSDK.sendEvent('fireDataLayerVariable', {
      variable: { userId },
    });
    gtmSDK.sendEvent('fireCustomEvent', {
      event: 'enrollmentSuccessPageViewed',
    });

    fireBingCustomConversionEvent();

    let cjEventCookie = cookies.get(CJ_EVENT_COOKIE);
    if (CJ_EVENT || cjEventCookie) {
      gtmSDK.sendEvent('fireCustomEvent', {
        event: {
          name: 'CJ Event',
          memberId: userId,
          CJEVENT: CJ_EVENT ? CJ_EVENT : cjEventCookie,
          valueProp: VALUE_PROP_QSP,
        },
      });
    }

    if (!IS_EMBEDDED) {
      executeOathScript(true);
    }

    if (!getValueFromStorage(CONVERSION_TRACKED)) {
      trackSuccessPageAttribution(formData);

      setValueInStorage(CONVERSION_TRACKED, 'true');
    }

    loadChimeDotComEnrollmentSuccess();
  }

  return { fireSuccessAttribution, deprecatedFireSuccessAttribution };
}
